import { useEffect, useState } from 'react';
import type { BlogPost } from '../../../mocks/blogPosts';

interface BlogModalProps {
  post: BlogPost;
  onClose: () => void;
}

interface Comment {
  id: string;
  postId: number;
  name: string;
  email: string;
  comment: string;
  date: string;
}

const BlogModal = ({ post, onClose }: BlogModalProps) => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [comment, setComment] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [commentsEnabled, setCommentsEnabled] = useState(true);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    
    // Load comments from localStorage
    const storedComments = localStorage.getItem('blogComments');
    if (storedComments) {
      const allComments: Comment[] = JSON.parse(storedComments);
      setComments(allComments.filter(c => c.postId === post.id));
    }

    // Check if comments are enabled for this post
    const commentsSettings = localStorage.getItem('blogCommentsSettings');
    if (commentsSettings) {
      const settings = JSON.parse(commentsSettings);
      setCommentsEnabled(settings[post.id] !== false);
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [post.id]);

  const handleSubmitComment = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim() || !email.trim() || !comment.trim()) return;

    const newComment: Comment = {
      id: Date.now().toString(),
      postId: post.id,
      name: name.trim(),
      email: email.trim(),
      comment: comment.trim(),
      date: new Date().toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      }),
    };

    // Save to localStorage
    const storedComments = localStorage.getItem('blogComments');
    const allComments: Comment[] = storedComments ? JSON.parse(storedComments) : [];
    allComments.push(newComment);
    localStorage.setItem('blogComments', JSON.stringify(allComments));

    // Update state
    setComments([...comments, newComment]);
    
    // Reset form
    setName('');
    setEmail('');
    setComment('');
    
    // Show success message
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      'home-birth': 'bg-emerald-100 text-emerald-700',
      'hospital-birth': 'bg-blue-100 text-blue-700',
      'vbac': 'bg-purple-100 text-purple-700',
      'postpartum': 'bg-rose-100 text-rose-700',
    };
    return colors[category] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="relative bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="sticky top-4 right-4 float-right z-10 w-10 h-10 flex items-center justify-center bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors cursor-pointer"
          aria-label="Close"
        >
          <i className="ri-close-line text-2xl text-gray-700"></i>
        </button>

        {/* Featured Image */}
        <div className="relative h-96 w-full overflow-hidden">
          <img
            src={post.image}
            alt={post.title}
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
        </div>

        {/* Content */}
        <div className="p-8 md:p-12">
          {/* Category Badge */}
          <div className="mb-4 flex items-center justify-between">
            <span className={`px-4 py-1.5 rounded-full text-sm font-medium ${getCategoryColor(post.category)}`}>
              {post.categoryLabel}
            </span>
            {comments.length > 0 && (
              <div className="flex items-center gap-2 text-gray-600">
                <i className="ri-chat-3-line text-lg"></i>
                <span className="text-sm font-medium">{comments.length} {comments.length === 1 ? 'Comment' : 'Comments'}</span>
              </div>
            )}
          </div>

          {/* Title */}
          <h2 className="text-4xl font-bold text-gray-900 mb-6 font-serif leading-tight">
            {post.title}
          </h2>

          {/* Meta Info */}
          <div className="flex items-center gap-6 mb-8 pb-8 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                <i className="ri-user-heart-line text-amber-700 text-xl"></i>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">{post.author}</p>
                <p className="text-xs text-gray-500">{post.date}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2 text-gray-500">
              <i className="ri-time-line"></i>
              <span className="text-sm">{post.readTime}</span>
            </div>
          </div>

          {/* Full Story Content */}
          <div className="prose prose-lg max-w-none">
            {post.fullStory.map((paragraph, index) => {
              if (paragraph.type === 'text') {
                return (
                  <p key={index} className="text-gray-700 leading-relaxed mb-6">
                    {paragraph.content}
                  </p>
                );
              } else if (paragraph.type === 'quote') {
                return (
                  <blockquote key={index} className="border-l-4 border-amber-700 pl-6 py-4 my-8 bg-amber-50 rounded-r-lg">
                    <p className="text-xl font-serif italic text-gray-800 leading-relaxed">
                      {paragraph.content}
                    </p>
                  </blockquote>
                );
              }
              return null;
            })}
          </div>

          {/* Share Section */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <p className="text-sm text-gray-600 mb-4">Share this story:</p>
            <div className="flex gap-3">
              <button className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-amber-700 hover:text-white transition-colors cursor-pointer">
                <i className="ri-facebook-fill text-lg"></i>
              </button>
              <button className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-amber-700 hover:text-white transition-colors cursor-pointer">
                <i className="ri-twitter-x-line text-lg"></i>
              </button>
              <button className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-amber-700 hover:text-white transition-colors cursor-pointer">
                <i className="ri-mail-line text-lg"></i>
              </button>
              <button className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-amber-700 hover:text-white transition-colors cursor-pointer">
                <i className="ri-link text-lg"></i>
              </button>
            </div>
          </div>

          {/* Comments Section */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <i className="ri-chat-3-line text-amber-700"></i>
              Comments ({comments.length})
            </h3>

            {/* Existing Comments */}
            {comments.length > 0 && (
              <div className="space-y-6 mb-8">
                {comments.map((c) => (
                  <div key={c.id} className="flex gap-4">
                    <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-amber-700 font-semibold text-sm">
                        {getInitials(c.name)}
                      </span>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <p className="font-semibold text-gray-900">{c.name}</p>
                        <span className="text-xs text-gray-500">{c.date}</span>
                      </div>
                      <p className="text-gray-700 leading-relaxed">{c.comment}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Comment Form or Disabled Message */}
            {commentsEnabled ? (
              <div className="bg-gray-50 rounded-lg p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Leave a Comment</h4>
                
                {showSuccess && (
                  <div className="mb-4 p-4 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-3">
                    <i className="ri-checkbox-circle-fill text-emerald-600 text-xl"></i>
                    <p className="text-emerald-800 text-sm font-medium">Your comment has been posted successfully!</p>
                  </div>
                )}

                <form onSubmit={handleSubmitComment} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="comment-name" className="block text-sm font-medium text-gray-700 mb-2">
                        Name *
                      </label>
                      <input
                        id="comment-name"
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-700 focus:border-transparent text-sm"
                        placeholder="Your name"
                      />
                    </div>
                    <div>
                      <label htmlFor="comment-email" className="block text-sm font-medium text-gray-700 mb-2">
                        Email * <span className="text-xs text-gray-500">(not displayed publicly)</span>
                      </label>
                      <input
                        id="comment-email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-700 focus:border-transparent text-sm"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label htmlFor="comment-text" className="block text-sm font-medium text-gray-700">
                        Comment *
                      </label>
                      <span className="text-xs text-gray-500">
                        {comment.length}/300
                      </span>
                    </div>
                    <textarea
                      id="comment-text"
                      value={comment}
                      onChange={(e) => {
                        if (e.target.value.length <= 300) {
                          setComment(e.target.value);
                        }
                      }}
                      required
                      rows={4}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-700 focus:border-transparent text-sm resize-none"
                      placeholder="Share your thoughts..."
                    />
                  </div>

                  <button
                    type="submit"
                    className="px-6 py-2.5 bg-amber-700 text-white rounded-lg font-medium hover:bg-amber-800 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Post Comment
                  </button>
                </form>
              </div>
            ) : (
              <div className="bg-gray-50 rounded-lg p-8 text-center">
                <i className="ri-chat-off-line text-4xl text-gray-400 mb-3 block"></i>
                <p className="text-gray-600 font-medium">Comments are closed for this story</p>
                <p className="text-sm text-gray-500 mt-1">Thank you for your interest</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogModal;